import React, { forwardRef, useMemo } from 'react';

import cx from 'classnames';

import FocalImage from 'components/Layouts/Form/FormBuilder/FocalImage';
import Button from 'components/shared/Button';
import placeholderSrc from 'components/shared/LandingPage/LeftSide/placeholder.svg';
import Subheadline from 'components/shared/LandingPage/LeftSide/Subheadline';
import { useTitleChangeModal } from 'hooks/useTitleChangeModal';
import i18n from 'i18n';
import { getAssetSrcFromId } from 'utils/asset';
import { EProposalItems } from 'utils/itineraryDetails/types';

import TravelDates from '../../../../components/shared/LandingPage/LeftSide/TravelDates';
import noop from '../../../../utils/noop';
import { useLandingV2Context } from '../../context';
import { getPrefix } from '../../utils';
import { usePageContext } from '../context';
import { EPageRole } from '../types';
import { useLeftSide } from './functions';
import PageHeadline from './Headline';
import s from './index.module.css';

export const LeftSide = forwardRef<HTMLDivElement>((props, ref) => {
  const {
    data,
    readonly,
    onChangeCoverImage,
    onChange,
    config: { isTextOverImageEditable, settings, defaultSubheadline },
  } = usePageContext();
  // TODO we probably need to get rid of this context
  const { data: proposal } = useLandingV2Context();
  const { leftSideProps, pictureSources, previewImage, primaryImageId } = useLeftSide();
  const canEdit = Boolean(isTextOverImageEditable);
  const showNavigationModal = useTitleChangeModal();
  const isGuestPortal = proposal?.type === EProposalItems.GuestPortal;
  const isCoverPage = data.role === EPageRole.COVER_PAGE;

  const pageSubheadline = useMemo(() => {
    if (data.role === EPageRole.COVER_PAGE) {
      return proposal.preparedFor ? `${i18n.t('proposal_content.prepared_for')} ${proposal.preparedFor}` : '';
    }

    return data.subheadline || defaultSubheadline || '';
  }, [data.role, data.subheadline, defaultSubheadline, proposal.preparedFor, i18n.version]);

  const pageHeadline = useMemo(() => {
    if (data.role == EPageRole.DAY) {
      return '';
    }

    return data.role === EPageRole.COVER_PAGE ? proposal.title : data.headline;
  }, [data.role, data.headline, proposal.title]);

  const handleHeaderClick = () => {
    if (isGuestPortal) return;
    if (!isCoverPage) return;

    showNavigationModal(readonly);
  };

  return (
    <div className={cx(s.left, getPrefix('left'))} {...leftSideProps} ref={ref}>
      <div className={s.innerLeft}>
        {!primaryImageId && <img src={placeholderSrc} alt="Placeholder" className={s.placeholder} />}
        {/*@ts-ignore*/}
        <FocalImage
          className={cx(s.img, 'custom-left-img')}
          src={primaryImageId ? getAssetSrcFromId(primaryImageId) : previewImage}
          point={data.primaryImageCoords}
          noLazyLoading
          pictureSources={pictureSources}
        />
        {!readonly && onChangeCoverImage && (
          <Button
            children={primaryImageId ? 'Replace Photo' : 'Choose Photo'}
            className={s.coverButton}
            size="medium"
            onClick={onChangeCoverImage}
            type="default"
          />
        )}
        {settings?.textOverImages && (
          <div className={s.headlineGroup} onClick={handleHeaderClick}>
            <Subheadline
              className={s.subheadline}
              value={pageSubheadline}
              readOnly={readonly}
              canEdit={canEdit}
              onEdit={onChange || noop}
              // @ts-expect-error
              style={{ '--branding_color': proposal?.brandSettings?.primaryColor }}
            />
            {(!readonly || pageHeadline) && (
              <PageHeadline
                className={s.headline}
                value={pageHeadline}
                readOnly={readonly}
                isEditable={canEdit}
                onEdit={onChange || noop}
              />
            )}
            {data.role === EPageRole.COVER_PAGE && proposal.travelDates && (
              <TravelDates
                value={proposal.travelDates}
                readOnly={readonly}
                canEdit={canEdit}
                onEdit={onChange || noop}
                className={s.travelDates}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});
